import { useTranslation } from 'react-i18next';
import { Play, RotateCw } from 'lucide-react';
import { Button } from '$lib/components/ui/Button';
import { StatusPill } from '$lib/components/ui/StatusPill';
import { StatusDot, severityCardBorder, severityFromStatus } from '$lib/components/ui/StatusDot';
import { uiRunStatus } from '$lib/api/adminScraper';
import type { PipelineHealthPortal, ScrapeRunDetail } from '$lib/api/adminScraper';
import { cn } from '$lib/lib/cn';
import { formatDateLong, formatNumber } from '$lib/lib/formatters';

export interface PortalCardProps {
  portal: PipelineHealthPortal;
  // Active run detail — only present (and only fetched) when the portal's
  // last_run_status is "running". Carries the per-stage progress JSONB so the
  // card can render an inline bar showing items done/total.
  activeRun?: ScrapeRunDetail | null;
  onStartFreshRun: (portalSlug: string) => void;
  onRetry: (portalSlug: string) => void;
}

// Sum of the most operator-meaningful stage counters — `scrape.listings_found`
// is the canonical "items processed" number once we've left the scrape stage,
// because diff/persist/enrich operate on the same set. Keeping the function
// near the card so the rule is documented next to the UI it drives.
function itemsProcessedFromProgress(
  progress: ScrapeRunDetail['stage_progress']
): number | null {
  if (!progress) return null;
  const found = progress.scrape?.listings_found;
  if (typeof found === 'number') return found;
  const pages = progress.scrape?.pages_fetched;
  if (typeof pages === 'number') return pages;
  return null;
}

export function PortalCard({ portal, activeRun, onStartFreshRun, onRetry }: PortalCardProps) {
  const { t } = useTranslation();
  const status = uiRunStatus(portal.last_run_status ?? 'pending', portal.error_count_last_run);
  const severity = severityFromStatus(status);
  const isRunning = status === 'running';
  const isFailed = status === 'failed';

  const itemsDone = activeRun ? itemsProcessedFromProgress(activeRun.stage_progress) : null;
  const itemsTotal = activeRun?.target_count ?? null;
  const stageId = activeRun?.current_stage ?? null;
  const stageLabel = stageId ? t(`admin.stage.${stageId}`) : null;
  const progressPct =
    itemsDone != null && itemsTotal != null && itemsTotal > 0
      ? Math.min(100, Math.round((itemsDone / itemsTotal) * 100))
      : null;

  const lastRunCopy = portal.last_run_at
    ? t('admin.portal.lastRun', { time: formatDateLong(portal.last_run_at) })
    : t('admin.portal.neverRun');

  return (
    <article
      data-testid={`portal-card-${portal.slug}`}
      data-severity={severity}
      aria-label={portal.headline}
      className={cn(
        'flex min-w-0 flex-col gap-2 rounded bg-surface p-4 shadow-elev-1',
        severityCardBorder(severity)
      )}
    >
      <header className="flex min-w-0 items-center gap-2">
        <StatusDot
          severity={severity}
          size="md"
          pulse={isRunning}
          ariaLabel={t(`admin.portal.${severity === 'failed' ? 'failed' : severity === 'degraded' ? 'degraded' : 'healthy'}`)}
        />
        <h3 className="truncate text-sm font-semibold text-fg">{portal.name}</h3>
        <StatusPill
          className="ml-auto"
          status={status}
          errorCount={portal.error_count_last_run}
          data-testid={`portal-card-${portal.slug}-pill`}
        />
      </header>

      <dl className="grid gap-1 text-2xs text-fg-muted">
        <div className="flex justify-between gap-2">
          <dt className="sr-only">{t('admin.portal.lastRun', { time: '' })}</dt>
          <dd className="num">{lastRunCopy}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="sr-only">{t('admin.portal.listingCount', { count: 0 })}</dt>
          <dd className="num">
            {t('admin.portal.listingCount', { count: portal.active_listings })}
          </dd>
          <dd>{t('admin.portal.nextSweepTonight')}</dd>
        </div>
      </dl>

      {isRunning && stageLabel && (
        <RunningProgress
          stageLabel={stageLabel}
          itemsDone={itemsDone}
          itemsTotal={itemsTotal}
          progressPct={progressPct}
        />
      )}

      <footer className="mt-1 flex items-center gap-2">
        {isFailed ? (
          <Button
            size="sm"
            variant="danger"
            data-testid={`portal-card-${portal.slug}-retry`}
            onClick={() => onRetry(portal.slug)}
          >
            <RotateCw size={12} aria-hidden />
            {t('admin.action.retry')}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="secondary"
            disabled={isRunning}
            data-testid={`portal-card-${portal.slug}-fresh`}
            onClick={() => onStartFreshRun(portal.slug)}
          >
            <Play size={12} aria-hidden />
            {t('admin.action.startFreshRun')}
          </Button>
        )}
      </footer>
    </article>
  );
}

interface RunningProgressProps {
  stageLabel: string;
  itemsDone: number | null;
  itemsTotal: number | null;
  progressPct: number | null;
}

function RunningProgress({ stageLabel, itemsDone, itemsTotal, progressPct }: RunningProgressProps) {
  const { t } = useTranslation();
  const itemsCopy =
    itemsDone != null && itemsTotal != null
      ? t('admin.portal.listingProgress', {
          done: formatNumber(itemsDone),
          total: formatNumber(itemsTotal)
        })
      : null;

  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={progressPct ?? undefined}
      aria-label={stageLabel}
      data-testid="portal-running-progress"
      className="mt-1"
    >
      <div className="flex items-center justify-between gap-2 text-2xs text-fg-muted">
        <span className="font-medium text-info">{stageLabel}</span>
        {itemsCopy && <span className="num">{itemsCopy}</span>}
      </div>
      <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-surface-3">
        <span
          className="block h-full bg-info transition-all duration-fast"
          style={{ width: `${progressPct ?? 5}%` }}
          aria-hidden
        />
      </div>
    </div>
  );
}
